import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
    IonIcon,
    IonList, 
    IonTitle, 
    IonHeader,
    IonItem,
    IonLabel,
    IonAvatar,
    IonModal,
    IonContent,
    IonButton,
    IonText,
    IonToggle
} from '@ionic/react';
import { 
    gridOutline, 
    briefcaseOutline, 
    logOutOutline,
    personOutline,
    moonOutline,
    sunnyOutline,
    walletOutline
} from 'ionicons/icons';
import { appConfig } from '../config/appConfig';

// Constants
const isNative = Capacitor.isNativePlatform();

interface CustomMenuProps {
    isCollapsed: boolean;
    onToggle: () => void;
}

interface UserInfo {
    username: string;
    email: string;
    full_name: string;
    role: string;
}

const CustomMenu: React.FC<CustomMenuProps> = ({ isCollapsed, onToggle }) => {
    const history = useHistory();
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(false);

    useEffect(() => {
        // Retrieve user info
        const storedUserInfo = localStorage.getItem('userInfo');
        if (storedUserInfo) {
            setUserInfo(JSON.parse(storedUserInfo));
        }

        // Check saved theme preference
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
            const isDark = savedTheme === 'dark';
            setIsDarkMode(isDark);
            applyTheme(isDark);
        } else {
            // Check system preference
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
            setIsDarkMode(prefersDark.matches);
            applyTheme(prefersDark.matches);
        }
    }, []);

    const applyTheme = (isDark: boolean) => {
        if (isDark) {
            document.body.classList.add('dark');
            document.body.classList.remove('light');
        } else {
            document.body.classList.add('light');
            document.body.classList.remove('dark');
        }
        localStorage.setItem('theme', isDark ? 'dark' : 'light');
    };

    const toggleTheme = () => {
        const newTheme = !isDarkMode;
        setIsDarkMode(newTheme);
        applyTheme(newTheme);
    };

    const handleLogout = () => {
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('jwtTokenExpiry');
        localStorage.removeItem('userInfo');
        // For logout, we use full path since it's a page reload
        window.location.href = isNative ? '/login' : '/login';
    };

    const handleNavigation = (path: string) => {
        // Remove leading slash for proper routing
        const cleanPath = path.startsWith('/') ? path.slice(1) : path;
        history.push(cleanPath);
        onToggle();
    };

    const openUserModal = () => {
        setIsUserModalOpen(true);
    };

    const renderProfitMenuItem = () => {
        if (!userInfo) return null;

        const userType = userInfo.role.toLowerCase();
        const canViewProfit = 
            userType === 'agent' || 
            userType === 'vendor';

        if (!canViewProfit) return null;

        return (
            <IonItem onClick={() => handleNavigation('profit')} lines="none">
                <IonIcon slot="start" icon={walletOutline} />
                <IonLabel>Profit Management</IonLabel>
            </IonItem>
        );
    };

    return (
        <div
            id="custom-menu"
            className={`custom-menu ${isCollapsed ? 'collapsed' : ''}`}
            style={{
                backgroundColor: 'var(--ion-background-color)',
                color: 'var(--ion-text-color)'
            }}
        >
            <IonHeader>
                <IonTitle 
                    className="ion-text-center"
                    style={{ 
                        padding: '20px 15px', 
                        color: 'var(--ion-text-color)',
                        borderBottomColor: 'var(--ion-color-medium)',
                        position: 'relative',
                        zIndex: 10  
                    }}
                >
                    ERP System
                </IonTitle>
            </IonHeader>
            
            {userInfo && (
                <div 
                    style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        padding: '15px', 
                        borderBottom: '1px solid var(--ion-color-medium)'
                    }}
                >
                    <IonAvatar 
                        onClick={openUserModal} 
                        style={{ 
                            marginRight: '15px', 
                            cursor: 'pointer',
                            width: '60px', 
                            height: '60px', 
                            background: 'var(--ion-color-medium)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <IonIcon 
                            icon={personOutline} 
                            style={{ 
                                color: 'var(--ion-text-color)', 
                                fontSize: '30px' 
                            }} 
                        />
                    </IonAvatar>
                    <div>
                        <h3 
                            style={{ 
                                margin: 0, 
                                color: 'var(--ion-text-color)', 
                                fontSize: '1rem' 
                            }}
                        >
                            {userInfo.full_name}
                        </h3>
                        <hr 
                            style={{ 
                                margin: '5px 0', 
                                borderColor: 'var(--ion-color-medium)', 
                                borderWidth: '1px',
                                borderStyle: 'solid',
                                opacity: 0.3
                            }} 
                        />
                        <p 
                            style={{ 
                                margin: 0, 
                                color: 'var(--ion-color-medium)', 
                                fontSize: '0.8rem' 
                            }}
                        >
                            {userInfo.role}
                        </p>
                    </div>
                </div>
            )}
            
            <IonList style={{ 
                position: 'relative', 
                zIndex: 1,
                marginTop: '-1px'
            }}>
                <IonItem onClick={() => handleNavigation('home')} lines="none">
                    <IonIcon slot="start" icon={gridOutline} />
                    <IonLabel>Dashboard</IonLabel>
                </IonItem>

                <IonItem onClick={() => handleNavigation('jobs')} lines="none">
                    <IonIcon slot="start" icon={briefcaseOutline} />
                    <IonLabel>Jobs</IonLabel>
                </IonItem>

                {renderProfitMenuItem()}

                <IonItem onClick={handleLogout} lines="none">
                    <IonIcon slot="start" icon={logOutOutline} style={{ color: 'var(--ion-color-danger)' }} />
                    <IonLabel>Logout</IonLabel>
                </IonItem>
            </IonList>

            {/* Theme Toggle Footer */}
            <div 
                style={{ 
                    position: 'absolute', 
                    bottom: 0, 
                    width: '100%', 
                    padding: '10px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    borderTop: '1px solid var(--ion-color-medium)', 
                    flexDirection: 'row',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', padding: '5px' }}>
                    <span style={{ color: 'var(--ion-text-color)', paddingRight: '10px', }}>
                        {isDarkMode ? 'Dark Mode' : 'Light Mode'}
                    </span>
                    <IonToggle 
                        checked={isDarkMode} 
                        onIonChange={toggleTheme}
                    />
                </div>
                
                <IonIcon 
                    icon={isDarkMode ? moonOutline : sunnyOutline} 
                    style={{ 
                        marginRight: '10px', 
                        color: 'var(--ion-text-color)', 
                        fontSize: '20px' 
                    }}
                    onClick={toggleTheme}
                />
            </div>

            <IonModal 
                isOpen={isUserModalOpen} 
                onDidDismiss={() => setIsUserModalOpen(false)}
            >
                <IonContent>
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                        <IonAvatar 
                            style={{ 
                                width: '100px', 
                                height: '100px', 
                                margin: '0 auto 20px',
                                background: 'var(--ion-color-medium)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}
                        >
                            <IonIcon 
                                icon={personOutline} 
                                style={{ 
                                    color: 'var(--ion-text-color)', 
                                    fontSize: '50px' 
                                }} 
                            />
                        </IonAvatar>
                        <IonText>
                            <h2>{userInfo?.full_name}</h2>
                            <p>Username: {userInfo?.username}</p>
                            <p>Email: {userInfo?.email}</p>
                            <p>Role: {userInfo?.role}</p>
                        </IonText>
                        <IonButton onClick={() => setIsUserModalOpen(false)}>Close</IonButton>
                    </div>
                </IonContent>
            </IonModal>
        </div>
    );
};

export default CustomMenu;
